import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { ScrollArea } from 'components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Plus, Trash2, Save, FileText, X } from 'lucide-react';
import { CustomSchema, CustomSchemaMetadata } from './hooks/useCustomSchema';
import { toast } from 'sonner';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Switch } from 'components/ui/switch';

interface SchemaField {
  key: string;
  rawField: string;
  title: string;
  explainer: string;
}

interface SchemaEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customSchema: CustomSchema;
  metadata: CustomSchemaMetadata;
  onSave: (schema: CustomSchema, metadata: CustomSchemaMetadata) => Promise<void>;
}

export function SchemaEditorDialog({
  open,
  onOpenChange,
  customSchema,
  metadata,
  onSave,
}: SchemaEditorDialogProps) {
  const [fields, setFields] = useState<SchemaField[]>([]);
  const [useOnlyCustomSchema, setUseOnlyCustomSchema] = useState(false);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPasteMode, setShowPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Initialize fields and metadata from customSchema when dialog opens
  useEffect(() => {
    if (open) {
      const schemaFields = Object.entries(customSchema)
        .filter(([key]) => key !== '_metadata')
        .map(([key, value]) => {
          if (!value || typeof value !== 'object' || !('title' in value)) {
            return null;
          }
          return {
            key,
            rawField: value.rawField || '',
            title: value.title,
            explainer: value.explainer,
          };
        })
        .filter((field): field is SchemaField => field !== null);
      setFields(schemaFields);
      setUseOnlyCustomSchema(metadata.useOnlyCustomSchema || false);
      setNote(metadata.note || '');
    }
  }, [open, customSchema, metadata]);

  const addField = () => {
    setFields([...fields, { key: '', rawField: '', title: '', explainer: '' }]);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, field: Partial<SchemaField>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...field };
    setFields(newFields);
  };

  const parsePastedData = () => {
    if (!pasteText.trim()) {
      toast.error('Indsæt venligst noget data');
      return;
    }

    const lines = pasteText.trim().split('\n');
    const newFields: SchemaField[] = [];

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Try to parse as CSV (rawField,key,title,explainer)
      if (trimmedLine.includes(',')) {
        const parts = trimmedLine.split(',').map((p) => p.trim());
        // Check if we have 4 parts (rawField,key,title,explainer)
        if (parts.length >= 4) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[2] || parts[1] || '',
            explainer: parts[3] || '',
          });
        }
        // 3 parts: (rawField,key,title)
        else if (parts.length === 3) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[2] || parts[1] || '',
            explainer: '',
          });
        }
        // 2 parts: (rawField,key)
        else if (parts.length === 2) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[1] || '',
            explainer: '',
          });
        }
        // 1 part: treat as rawField
        else {
          newFields.push({
            rawField: parts[0] || '',
            key: '',
            title: '',
            explainer: '',
          });
        }
      }
      // Try to parse as TSV (rawField\tkey\ttitle\texplainer)
      else if (trimmedLine.includes('\t')) {
        const parts = trimmedLine.split('\t').map((p) => p.trim());
        // Check if we have 4 parts
        if (parts.length >= 4) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[2] || parts[1] || '',
            explainer: parts[3] || '',
          });
        }
        // 3 parts: (rawField,key,title)
        else if (parts.length === 3) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[2] || parts[1] || '',
            explainer: '',
          });
        }
        // 2 parts: (rawField,key)
        else if (parts.length === 2) {
          newFields.push({
            rawField: parts[0] || '',
            key: parts[1] || '',
            title: parts[1] || '',
            explainer: '',
          });
        }
        // 1 part: treat as rawField
        else {
          newFields.push({
            rawField: parts[0] || '',
            key: '',
            title: '',
            explainer: '',
          });
        }
      }
      // Otherwise treat as just a rawField (simple list)
      else {
        newFields.push({
          rawField: trimmedLine,
          key: '',
          title: '',
          explainer: '',
        });
      }
    });

    if (newFields.length === 0) {
      toast.error('Ingen gyldige felter fundet');
      return;
    }

    // Add to existing fields
    setFields([...fields, ...newFields]);
    setPasteText('');
    setShowPasteMode(false);
    toast.success(`${newFields.length} felter tilføjet`);
  };

  const handleSave = async () => {
    // Validate fields
    const invalidFields = fields.filter((f) => !f.key.trim() || !f.title.trim());
    if (invalidFields.length > 0) {
      toast.error('Alle felter skal have en nøgle og titel');
      return;
    }

    // Check for duplicate keys
    const keys = fields.map((f) => f.key.trim());
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
    if (duplicates.length > 0) {
      toast.error(`Duplikerede nøgler fundet: ${duplicates.join(', ')}`);
      return;
    }

    setIsSaving(true);
    try {
      // Convert fields array to schema object
      const newSchema: CustomSchema = {};
      fields.forEach((field) => {
        newSchema[field.key.trim()] = {
          rawField: field.rawField.trim() || undefined,
          title: field.title.trim(),
          explainer: field.explainer.trim(),
        };
      });

      const newMetadata: CustomSchemaMetadata = {
        useOnlyCustomSchema,
        note: note.trim(),
      };

      await onSave(newSchema, newMetadata);
      toast.success('Skema gemt');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving schema:', error);
      toast.error('Fejl ved gemning af skema');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Rediger tilpasset skema</DialogTitle>
          <DialogDescription>
            Tilføj eller rediger felter til dit tilpassede skema. Nøglen skal matche nøglen fra
            sensor data.
          </DialogDescription>
        </DialogHeader>

        {/* Metadata Section */}
        <div className="space-y-4 border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use-only-custom" className="text-sm font-medium">
                Brug kun tilpasset skema
              </Label>
              <p className="text-xs text-muted-foreground">
                Ignorer standard skema og vis kun de definerede felter
              </p>
            </div>
            <Switch
              id="use-only-custom"
              checked={useOnlyCustomSchema}
              onCheckedChange={setUseOnlyCustomSchema}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="schema-note" className="text-sm">
              Note
            </Label>
            <Textarea
              id="schema-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tilføj noter om dette skema..."
              rows={2}
              className="text-xs resize-none"
            />
          </div>
        </div>

        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-4">
            {showPasteMode && (
              <Card className="border-primary">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Indsæt data</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowPasteMode(false);
                        setPasteText('');
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertDescription className="text-xs">
                      <strong>Formater:</strong>
                      <br />• <strong>Liste:</strong> Et råfelt per linje (f.eks. cm Te l)
                      <br />• <strong>CSV (2 kolonner):</strong> råfelt,nøgle
                      <br />• <strong>CSV (3 kolonner):</strong> råfelt,nøgle,titel
                      <br />• <strong>CSV (4 kolonner):</strong> råfelt,nøgle,titel,forklaring
                      <br />• <strong>TSV:</strong> Samme som CSV men med TAB
                      <br />
                      <br />
                      Eksempel:{' '}
                      <code className="text-xs">cm,level,Niveau,Vandniveau målt i centimeter</code>
                    </AlertDescription>
                  </Alert>
                  <Textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder="Indsæt CSV, TSV eller liste af råfelter her...&#10;Eksempel:&#10;cm&#10;Te&#10;l"
                    rows={8}
                    className="font-mono text-xs"
                  />
                  <Button onClick={parsePastedData} className="w-full gap-2">
                    <FileText className="h-4 w-4" />
                    Tilføj felter fra indsætning
                  </Button>
                </CardContent>
              </Card>
            )}

            {fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>
                  Ingen felter endnu. Klik "Tilføj felt" eller "Indsæt data" for at komme i gang.
                </p>
              </div>
            ) : (
              fields.map((field, index) => (
                <Card key={index} className="p-3">
                  <div className="space-y-3">
                    {/* Header with field number and delete button */}

                    {/* Three fields in one row */}
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-3">
                        <Input
                          id={`rawField-${index}`}
                          value={field.rawField}
                          onChange={(e) => updateField(index, { rawField: e.target.value })}
                          placeholder="cm"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="col-span-3">
                        <Input
                          id={`key-${index}`}
                          value={field.key}
                          onChange={(e) => updateField(index, { key: e.target.value })}
                          placeholder="level"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="col-span-5">
                        <Input
                          id={`title-${index}`}
                          value={field.title}
                          onChange={(e) => updateField(index, { title: e.target.value })}
                          placeholder="Niveau"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="col-span-1">
                        <div className="flex items-center justify-between">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeField(index)}
                            className=""
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Explainer below */}
                    <div>
                      <Textarea
                        id={`explainer-${index}`}
                        value={field.explainer}
                        onChange={(e) => updateField(index, { explainer: e.target.value })}
                        placeholder="Beskriv hvad dette felt betyder..."
                        rows={1}
                        className="text-xs resize-none"
                      />
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={addField} className="gap-2">
              <Plus className="h-4 w-4" />
              Tilføj felt
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPasteMode(!showPasteMode)}
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              Indsæt data
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Annuller
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-2">
              <Save className="h-4 w-4" />
              {isSaving ? 'Gemmer...' : 'Gem skema'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
